import { isEmpty } from 'lodash-es';
import { forwardRef, useMemo } from 'react';
import { EventVenueMap } from 'src/components/Events/VenueMap/EventVenueMap';
import { useVenueMapHandlers } from 'src/components/Listings/MapWithCompListingsPreview/MapWithCompListingsPreview.hooks';
import { useEventMapContext } from 'src/contexts/EventMapContext';
import { useComparableListingsVenueMapColor } from 'src/hooks/useComparableListingsVenueMapColor';
import { CompListing, Listing, SectionInfo } from 'src/WebApiController';

import * as styles from './ListingTilesVenueMap.css';

type ListingTilesVenueMapProps = {
  eventListings: Listing[];
  compListings: CompListing[];
  selectedSections: SectionInfo[];
  onUpdateSelectedSections: (sections: SectionInfo[]) => void;
};

export const ListingTilesVenueMap = forwardRef<
  HTMLDivElement,
  ListingTilesVenueMapProps
>(function ListingTilesVenueMap(
  { eventListings, compListings, selectedSections, onUpdateSelectedSections },
  ref
) {
  const { venueMapInfo } = useEventMapContext();

  const {
    onSectionClicked,
    onToggleMirrors,
    onSectionHovered,
    onSectionTapAndHold,
  } = useVenueMapHandlers(
    selectedSections,
    undefined,
    venueMapInfo,
    onUpdateSelectedSections
  );

  // Disable rainbow color when there is section selections in the map
  const disableRainbowColor = useMemo(() => {
    return !isEmpty(selectedSections);
  }, [selectedSections]);

  const getColor = useComparableListingsVenueMapColor(
    eventListings ?? [],
    compListings,
    undefined,
    disableRainbowColor
  );

  const markedSectionIds = useMemo(
    () => selectedSections.map((section: SectionInfo) => section.id),
    [selectedSections]
  );

  return (
    <div
      ref={ref}
      style={{ width: '100%', aspectRatio: '5/4', overflow: 'hidden' }}
    >
      <EventVenueMap
        isCondensedView
        selectedSectionIds={[]}
        markedSectionIds={markedSectionIds}
        onSectionClicked={onSectionClicked}
        onSectionTapAndHold={onSectionTapAndHold}
        onSectionHovered={onSectionHovered}
        onToggleMirrors={onToggleMirrors}
        setSelectedSections={onUpdateSelectedSections}
        getColor={getColor}
        allowDrawLine={false}
        condensedControl
        mapActionContainerClassname={styles.actionContainer}
      />
    </div>
  );
});
